import { Builder } from '@builder.io/react';
import { BuilderComponentState } from '@builder.io/react/dist/types/src/components/builder-component.component';
import { useGSAP } from '@gsap/react';
import classNames from 'classnames';
import gsap from 'gsap';
import ScrollToPlugin from 'gsap/dist/ScrollToPlugin';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SwiperCore from 'swiper';
import { Autoplay, EffectFade, FreeMode, Pagination, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { BusinessIndustries } from '../../types/BusinessIndustries';
import BusinessHubTrustedBrand from '../business-hub-trusted-brand/business-hub-trusted-brand';
import { BusinessHubBuildForIndustryBuilderConfig } from './business-hub-build-for-industry.builder-component';
import styles from './business-hub-build-for-industry.module.scss';
import { IndustriesItem } from './industries-item';

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollToPlugin);

const options: SwiperOptions = {
    slidesPerView: 'auto',
    allowTouchMove: true,
    centeredSlides: true,
    centeredSlidesBounds: true,
    centerInsufficientSlides: true,
    initialSlide: 1,
    freeMode: {
        enabled: true,
        sticky: false,
    },
    modules: [FreeMode],
    watchOverflow: false,
    grabCursor: true,

    breakpoints: {
        608: {
            initialSlide: 0,
            watchOverflow: true,
        },
    },
};

interface SSProps {
    builderState?: BuilderComponentState;
}

export interface BusinessHubBuildForIndustryProps extends SSProps {
    industries?: BusinessIndustries[];
    showLearnMore?: boolean;
}

const BusinessHubBuildForIndustry: FC<BusinessHubBuildForIndustryProps> = (props) => {
    const { industries: industriesProps, showLearnMore = true, builderState } = props;
    const config = builderState?.state[BusinessHubBuildForIndustryBuilderConfig.name];

    const containerRef = useRef<HTMLDivElement>(null);
    const wordsRef = useRef<HTMLDivElement>(null);
    const [trustedBrandSwiper, setTrustedBrandSwiper] = useState<SwiperCore>();
    const [industryCardSwiper, setIndustryCardSwiper] = useState<SwiperCore>();
    const [industryListSwiper, setIndustryListSwiper] = useState<SwiperCore>();
    const [activeIndex, setActiveIndex] = useState(-1);
    const clickIndex = useRef<number | null>(null);

    useGSAP(
        () => {
            if (window.location.hash.startsWith('#industries')) {
                setTimeout(() => {
                    const elem = document.getElementById('industries');
                    if (!elem) return;
                    const position = elem.getBoundingClientRect();
                    gsap.to(window, { duration: 1, scrollTo: position.top + window.scrollY - 116 });
                }, 500);
            }
        },
        { scope: containerRef }
    );

    useEffect(() => {
        if (!containerRef.current) return;
        if (!industryListSwiper) return;
        if (activeIndex === -1) {
            industryListSwiper.setProgress(0, 500);
            return;
        }

        const parent = containerRef.current.querySelector(`.${styles.carouselContainer}`);
        const elem = containerRef.current.querySelector(`.${styles.industries} .${styles.carouselItem}:nth-child(${activeIndex + 1})`);
        if (!elem || !parent) return;

        const parentBounds = parent.getBoundingClientRect();
        const childBounds = elem.getBoundingClientRect();

        const center = childBounds.x + childBounds.width / 2;
        const centerParent = parentBounds.width;

        if (childBounds.x < 0) {
            industryListSwiper.setProgress(0.1, 500);
        } else {
            industryListSwiper.setProgress(Math.abs((center / centerParent) * (parentBounds.width / industryListSwiper.width)), 500);
        }
    }, [activeIndex, industryListSwiper, containerRef]);

    const industries =
        useMemo(() => {
            let indus = industriesProps;
            if (Builder.isEditing || Builder.isPreviewing) {
                indus = industriesProps || (config?.industries as BusinessIndustries[]);
            }

            if (!indus) return indus;

            return indus.sort((a, b) => {
                if (a.name < b.name) {
                    return -1;
                }
                if (a.name > b.name) {
                    return 1;
                }
                return 0;
            });
        }, [industriesProps, config]) || [];

    const changeWords = useCallback(() => {
        if (!wordsRef.current) return;

        const words = wordsRef.current.querySelectorAll(`span`);

        if (!words.length) return;

        let realIndex = activeIndex + 1;

        words[realIndex].classList.remove(styles.isHidden);
        words[realIndex].classList.add(styles.isVisible);

        for (let index = 0; index < words.length; index++) {
            const element = words[index];
            if (index === realIndex) continue;

            element.classList.add(styles.isHidden);
            element.classList.remove(styles.isVisible);
        }
    }, [wordsRef, activeIndex]);

    useEffect(() => {
        changeWords();
    }, [changeWords, activeIndex]);

    useEffect(() => {
        if (!industryCardSwiper) return;
        if (!containerRef.current) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries)
                entries.forEach((entry) => {
                    if (entry.intersectionRatio > 0) {
                        industryCardSwiper.params.autoplay = { delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true };
                        industryCardSwiper.update();
                        industryCardSwiper.autoplay.start();
                        observer.disconnect();
                    }
                });
        });

        observer.observe(containerRef.current);

        return () => {
            observer.disconnect();
        };
    }, [containerRef, industryCardSwiper]);

    return (
        <div className={styles.container} ref={containerRef}>
            <div ref={wordsRef} className={styles.wordsContainer}>
                <div>Built for&nbsp;</div>
                <div className={styles.wordsWrapper}>
                    <span className={styles.isVisible}>your industry.</span>
                    {industries?.map(({ name }, index) => (
                        <span key={`${name}-${index}`}>{name?.toLowerCase()}.</span>
                    ))}
                </div>
            </div>
            <div className={styles.carouselContainer}>
                <Swiper
                    {...options}
                    onInit={(swiper: SwiperCore) => {
                        swiper.setProgress(0.5);
                        setIndustryListSwiper(swiper);
                    }}
                >
                    <SwiperSlide className={styles.mdHide}></SwiperSlide>
                    <SwiperSlide>
                        <div className={styles.industries}>
                            {industries.map(({ name }, index) => {
                                return (
                                    <div
                                        key={name}
                                        className={classNames(styles.carouselItem, { [styles.active]: index === activeIndex || (index === 0 && activeIndex === -1) })}
                                        onClick={() => {
                                            clickIndex.current = index;
                                            industryCardSwiper?.slideTo(index);
                                        }}
                                    >
                                        {name}
                                    </div>
                                );
                            })}
                        </div>
                    </SwiperSlide>
                    <SwiperSlide className={styles.mdHide}></SwiperSlide>
                </Swiper>
            </div>
            <div className={styles.industriesContainer}>
                <Swiper
                    slidesPerView={1}
                    speed={300}
                    // autoplay={}
                    modules={[EffectFade, Thumbs, Pagination, Autoplay]}
                    pagination={{
                        clickable: true,
                        el: `.${styles.pagination}`,
                    }}
                    onInit={(swiper: SwiperCore) => {
                        setIndustryCardSwiper(swiper);
                    }}
                    thumbs={{ swiper: trustedBrandSwiper }}
                    onSlideChange={(swiper: SwiperCore) => {
                        if (clickIndex.current === null && swiper.realIndex === 0) {
                            setActiveIndex(-1);
                            swiper.autoplay.stop();
                            setTimeout(() => {
                                setActiveIndex(0);
                                swiper.autoplay.start();
                            }, 4000);
                            return;
                        }

                        clickIndex.current = null;
                        setActiveIndex(swiper.realIndex);
                    }}
                >
                    {industries.map((industry) => {
                        return (
                            <SwiperSlide key={industry.name}>
                                <IndustriesItem key={industry.name} {...industry} showQuote showLearnMore={showLearnMore} />
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div>

            <div className={classNames(styles.trustedBrandContainer, { [styles.hide]: activeIndex !== -1 && !industries[activeIndex]?.trustedBrands?.length })}>
                <div className={classNames(styles.trustedTitle)}>Trusted by</div>
                <Swiper
                    slidesPerView={1}
                    draggable={false}
                    noSwiping
                    allowTouchMove={false}
                    effect={'fade'}
                    fadeEffect={{ crossFade: true }}
                    modules={[EffectFade]}
                    onInit={(swiper: SwiperCore) => {
                        setTrustedBrandSwiper(swiper);
                    }}
                >
                    {industries.map((industry) => (
                        <SwiperSlide key={industry.name} className={styles.trustedBrandSlide}>
                            <BusinessHubTrustedBrand trustedBrands={industry.trustedBrands} className={styles.trustedBrand} />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            <div className={styles.pagination}></div>
        </div>
    );
};

export default BusinessHubBuildForIndustry;
